/**
 * HOW A PERSON IS NAMED — by their name, else the address they are reached
 * through. Never by `users.username` (card `t_e56dc2b2`, PLAN.md §3/D6).
 *
 * `users.username` is a **derived, invisible column** (hub card `t_a8eb402a`):
 * the hub writes the email into it on the invite path (`UserInviteService`), a
 * projection row gets `cloud_<hash>`, the commissioning anchor gets
 * `bootstrap`, and a machine row keeps whatever it was created with. None of
 * those is what a person is called, and the owner's rule is that the handle
 * never reaches a screen:
 *
 *     personName({ fullName: 'Maja', email: 'maja@example.com' })   →  'Maja'
 *     personName({ fullName: null,   email: 'maja@example.com' })   →  'maja@example.com'
 *     personName({ fullName: null,   email: null, username: 'bootstrap' })
 *                                                                  →  the fallback
 *
 * The initials avatar derives from the SAME string (`personInitials`), so the
 * two can never disagree — the pair the People list, the chat panels and the
 * settings directory all render by.
 */

/** The slice of a person object these rules read. */
export interface NamedPerson {
  fullName?: string | null;
  email?: string | null;
}

/** The one naming rule: the name, else the address, else the caller's fallback. */
export function personName(person: NamedPerson | null | undefined, fallback = ''): string {
  return person?.fullName?.trim() || person?.email?.trim() || fallback;
}

/**
 * Initials for an avatar, from the string the person is named by. A name gives
 * the first letters of its first two words ("Maja Novak" → `MN`); a single token
 * (an address, or a one-word name) gives its first letter — one rule, so one
 * person cannot read two ways. (The home page's presence chips used to share
 * it; they left with plan `HOME-ONLY-SERVER-CARDS.md` §3 A2.)
 */
export function personInitials(name: string): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  return parts
    .map((part) => part[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();
}
